// RB-MY01-PalmGarden.web/src/pages/Profile.tsx
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  Typography, 
  Box, 
  Paper, 
  Avatar, 
  Button, 
  TextField,
  Alert,
  Divider
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { GridContainer, GridItem } from '../components/CustomGrid';

const Profile: React.FC = () => {
  const { user, isLoading } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [successMessage, setSuccessMessage] = useState('');
  
  const handleSave = () => {
    // In a real app, you would call an API to update the user profile
    // For now, we'll just simulate a successful update
    setTimeout(() => {
      setIsEditing(false);
      setSuccessMessage('Profile updated successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    }, 500);
  };
  
  if (isLoading) {
    return <Typography>Loading profile...</Typography>;
  }
  
  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Your Profile
      </Typography>
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      
      <Paper sx={{ p: 3, mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Avatar sx={{ width: 80, height: 80, mr: 2 }}>
            <AccountCircleIcon sx={{ fontSize: 60 }} />
          </Avatar>
          <Box>
            <Typography variant="h5">{user?.name}</Typography>
            <Typography variant="body1" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        {isEditing ? (
          <GridContainer spacing={2}>
            <GridItem xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </GridItem>
            <GridItem xs={12}>
              <TextField
                fullWidth
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled // Email is typically not editable in Auth0
              />
            </GridItem>
            <GridItem xs={12}>
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button 
                  variant="contained" 
                  onClick={handleSave}
                >
                  Save Changes
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </Button>
              </Box>
            </GridItem>
          </GridContainer>
        ) : (
          <Box>
            <Typography variant="h6" gutterBottom>
              Account Information
            </Typography>
            
            <GridContainer spacing={2}>
              <GridItem xs={12} sm={3}>
                <Typography variant="body1" color="text.secondary">
                  Name
                </Typography>
              </GridItem>
              <GridItem xs={12} sm={9}>
                <Typography variant="body1">
                  {user?.name}
                </Typography>
              </GridItem>
              
              <GridItem xs={12} sm={3}>
                <Typography variant="body1" color="text.secondary">
                  Email
                </Typography>
              </GridItem>
              <GridItem xs={12} sm={9}>
                <Typography variant="body1">
                  {user?.email}
                </Typography>
              </GridItem>
              
              <GridItem xs={12} sm={3}>
                <Typography variant="body1" color="text.secondary">
                  Account Type
                </Typography>
              </GridItem>
              <GridItem xs={12} sm={9}>
                <Typography variant="body1">
                  {user?.isAdmin ? 'Administrator' : 'Standard User'}
                </Typography>
              </GridItem>
              
              <GridItem xs={12}>
                <Button 
                  variant="contained" 
                  onClick={() => setIsEditing(true)}
                  sx={{ mt: 2 }}
                >
                  Edit Profile
                </Button>
              </GridItem>
            </GridContainer>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default Profile;